import { styled, css } from 'styled-components';
import type { ComponentPropsWithoutRef } from 'react';
import { isNotNil } from '@wistia/type-guards';

// brandmark = the Wistia flags icon
// logotype = the "wistia" text

// because the flags are shorter than the logo text we need to have a 2nd path (and viewBox),
// for when the brandmark is rendered without the logotype (paths below look similar but are unique)
const renderBrandmark = (brandmarkColor: string, iconOnly: boolean) => {
  if (iconOnly) {
    return (
      <g
        data-testid="ui-wistia-logo-brandmark"
        fill={brandmarkColor}
      >
        <path d="M16.09 17.1h-5.2c-1.58 0-3.08.68-4.11 1.87L.21 26.53c4.78.25 9.78.25 13.3.25 18.31 0 20.89-11.27 20.89-16.55-1.59 1.93-6.06 6.87-18.32 6.87ZM32.14 0c-.08.92-.59 4.69-11.31 4.69-8.72 0-12.24 0-20.83-.17l6.44 7.4a6.657 6.657 0 0 0 4.96 2.3c2.13.03 5.05.06 5.53.06 11.01 0 17.19-5.05 17.19-9.89 0-2.01-.67-3.44-1.97-4.4Z" />
      </g>
    );
  }

  return (
    <g
      data-testid="ui-wistia-logo-brandmark"
      fill={brandmarkColor}
    >
      <path d="M16.09 21.37h-5.2c-1.58 0-3.08.68-4.11 1.87L.21 30.8c4.78.25 9.78.25 13.3.25 18.31 0 20.89-11.27 20.89-16.55-1.59 1.93-6.06 6.87-18.32 6.87Zm16.05-17.1c-.08.92-.59 4.69-11.31 4.69-8.72 0-12.24 0-20.83-.17l6.44 7.4a6.657 6.657 0 0 0 4.96 2.3c2.13.03 5.05.06 5.53.06 11.01 0 17.19-5.05 17.19-9.89 0-2.01-.67-3.44-1.97-4.4Z" />
    </g>
  );
};

const renderLogotype = (logotypeColor: string, iconOnly: boolean) => {
  if (iconOnly) {
    return null;
  }

  return (
    <g
      data-testid="ui-wistia-logo-logotype"
      fill={logotypeColor}
    >
      <path d="M70.16 8.66v15.18c0 1.68-.35 3.09-1.05 4.23a6.612 6.612 0 0 1-2.85 2.54c-1.19.55-2.52.82-4.01.82s-2.8-.28-4.01-.85a6.655 6.655 0 0 1-3.11-2.96c-.08.15-.16.29-.24.42a6.552 6.552 0 0 1-2.87 2.54c-1.2.56-2.54.85-4.01.85s-2.8-.27-3.94-.82a6.214 6.214 0 0 1-2.71-2.52c-.67-1.14-1.01-2.56-1.02-4.25l-.22-15.18h7.34V22.3c0 .82.19 1.37.56 1.67.39.28.85.42 1.38.42s1.02-.15 1.45-.45c.43-.3.65-.85.65-1.65V8.65h7.3v13.64c0 .8.22 1.35.65 1.65.43.3.91.45 1.45.45s.99-.14 1.36-.42c.39-.3.58-.85.58-1.67V8.66h7.34Zm2.45 0v22.26h7.34V8.66h-7.34Zm5.67-1.87c.61-.3 1.08-.71 1.42-1.25.36-.55.53-1.19.53-1.94s-.18-1.34-.53-1.89A3.43 3.43 0 0 0 78.28.44c-.59-.3-1.25-.45-1.98-.45s-1.42.15-2.02.45c-.59.3-1.07.72-1.42 1.27-.36.55-.53 1.18-.53 1.89 0 1.1.38 1.97 1.13 2.63.76.65 1.71.98 2.85.98.73 0 1.39-.14 1.98-.42Zm8.86 1.96c-1.42.4-2.6 1.11-3.54 2.14-.93 1.02-1.4 2.4-1.4 4.12 0 1.11.17 2.09.51 2.94.36.85.82 1.62 1.38 2.34.22.28.55.65.98 1.11.37.4.64.72.8.96.18.24.27.47.27.69 0 .5-.4.87-1.2 1.09-.8.21-1.62.31-2.47.31-.1-.01-.22-.02-.33-.02l1.02 6.94c.42.07.92.11 1.51.11 1.9 0 3.6-.28 5.1-.85 1.5-.56 2.68-1.42 3.54-2.56.88-1.14 1.31-2.55 1.31-4.23 0-.68-.07-1.31-.22-1.87-.13-.58-.32-1.09-.56-1.54a6.64 6.64 0 0 0-.8-1.27c-.3-.37-.74-.82-1.34-1.36-.39-.33-.67-.59-.85-.8-.18-.22-.27-.45-.27-.67 0-.46.26-.79.78-.98.53-.19 1.17-.29 1.91-.29.25 0 .51.01.78.04l-.71-6.88a10.4 10.4 0 0 0-1.56-.11c-1.66 0-3.21.21-4.65.62Zm19.54 15.71c-.99 0-1.71-.23-2.14-.69-.42-.47-.62-1.18-.62-2.11v-6.57h4.21V8.66h-4.21V3.38l-7.34 1.85V24.1c0 2.45.47 4.29 1.4 5.52.95 1.22 2.45 1.83 4.49 1.83.95 0 1.86-.07 2.74-.22.88-.13 1.62-.34 2.25-.62l1.38-6.3c-.55.1-1.27.16-2.16.16Zm4.13-15.8v22.26h7.34V8.66h-7.34Zm5.67-1.87c.61-.3 1.08-.71 1.42-1.25.36-.55.53-1.19.53-1.94s-.18-1.34-.53-1.89a3.43 3.43 0 0 0-1.42-1.27c-.59-.3-1.25-.45-1.98-.45s-1.42.15-2.02.45c-.59.3-1.07.72-1.42 1.27-.36.55-.53 1.18-.53 1.89 0 1.1.38 1.97 1.14 2.63.76.65 1.71.98 2.85.98.73 0 1.39-.14 1.98-.42Zm27.51 1.87v22.26h-7.34v-2.28c-.41.43-.85.83-1.34 1.19-1.44 1.07-3.12 1.6-5.05 1.6s-3.61-.52-5.1-1.56c-1.48-1.05-2.63-2.47-3.45-4.25-.82-1.78-1.22-3.73-1.22-5.85s.41-4.07 1.22-5.83c.82-1.78 1.97-3.19 3.45-4.23 1.48-1.05 3.18-1.58 5.1-1.58s3.61.53 5.05 1.6c.48.36.93.75 1.34 1.19V8.66h7.34Zm-7.1 11.11c0-.8-.19-1.53-.56-2.18-.37-.67-.88-1.19-1.54-1.58-.64-.39-1.34-.58-2.09-.58s-1.45.19-2.09.58c-.64.39-1.15.91-1.54 1.58-.37.67-.56 1.39-.56 2.18s.19 1.51.56 2.18c.39.67.9 1.19 1.54 1.58.64.39 1.34.58 2.09.58s1.45-.19 2.09-.58c.65-.39 1.16-.91 1.54-1.56.37-.67.56-1.4.56-2.2Z" />
    </g>
  );
};

// viewBox sizes differ depending on which parts of the logo are visible
const computedViewBox = (iconOnly: boolean) => {
  if (iconOnly) {
    return '0 0 34.4 26.78';
  }

  return '0 0 144 31.47';
};

type WistiaLogoComponentProps = {
  height: number;
  $hoverColor: string | undefined;
  $opticallyCentered: boolean;
  $iconOnly: boolean;
};

const WistiaLogoComponent = styled.svg<WistiaLogoComponentProps>`
  height: ${({ height }) => `${height}px`};

  /* ensure it will always fit on mobile */
  max-height: 22vw;
  max-width: 100%;

  &:hover {
    path {
      ${({ $hoverColor }) => ($hoverColor !== undefined ? `fill: ${$hoverColor}` : null)};
    }
  }

  ${({ $opticallyCentered, $iconOnly }) => {
    if ($opticallyCentered) {
      if ($iconOnly) {
        return css`
          aspect-ratio: 1;
          padding: 11.85% 3.12% 13.91%;
        `;
      }
      return css`
        aspect-ratio: 127 / 32;
      `;
    }
    return null;
  }}
`;

export type WistiaLogoProps = Omit<ComponentPropsWithoutRef<'svg'>, 'height' | 'href'> & {
  /**
   * An optional accessible description for the logo
   */
  description?: string;
  /**
   * Height, in pixels, of the logo
   */
  height?: number;
  /**
   * If provided, will wrap the logo in a link
   */
  href?: string;
  /**
   * @ignore
   * Change the color when hovering over the logo
   */
  hoverColor?: string;
  /**
   * Display just the icon portion of the logo (ie. hide `Wistia` text)
   * note: there is no analogue to this because by brand guidelines, the
   * `Wistia` text must not be displayed without the "flags" iconography
   */
  iconOnly?: boolean;

  /**
   * An accessible title for the logo (defaults to "Wistia Logo")
   */
  title?: string;
  /**
   * Style of the logo
   */
  variant?: 'dark' | 'light' | 'standard';
  /**
   * If true, the logo will be optically centered within its container
   */
  opticallyCentered?: boolean;
};

/**
 * Render the Wistia logo in various ways.
 */
export const WistiaLogo = ({
  description = undefined,
  height = 100,
  hoverColor = undefined,
  href = undefined,
  iconOnly = false,
  opticallyCentered = false,
  title = 'Wistia Logo',
  variant = 'standard',
  ...props
}: WistiaLogoProps) => {
  // can't use `useThemeColor` since these are not in our color palette yet
  const primaryColor = '#2949e5';
  const darkColor = '#000934';
  const lightColor = '#ffffff';

  const VARIANT_COLORS = {
    standard: {
      brandmark: primaryColor,
      logotype: darkColor,
    },
    dark: {
      brandmark: darkColor,
      logotype: darkColor,
    },
    light: {
      brandmark: lightColor,
      logotype: lightColor,
    },
  };
  const brandmarkColor = VARIANT_COLORS[variant].brandmark;
  const logotypeColor = VARIANT_COLORS[variant].logotype;

  const Logo = (
    <WistiaLogoComponent
      $hoverColor={hoverColor}
      $iconOnly={iconOnly}
      $opticallyCentered={opticallyCentered}
      height={height}
      preserveAspectRatio={opticallyCentered ? 'xMidYMin' : undefined}
      role="img"
      viewBox={computedViewBox(iconOnly)}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <title>{title}</title>
      {isNotNil(description) ? <desc>{description}</desc> : null}
      {renderBrandmark(brandmarkColor, iconOnly)}
      {renderLogotype(logotypeColor, iconOnly)}
    </WistiaLogoComponent>
  );

  // wrap logo in a link if href prop is present
  return href !== undefined ? <a href={href}>{Logo}</a> : Logo;
};

WistiaLogo.displayName = 'WistiaLogo';
